import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  ShieldCheck, ChartLineUp, Buildings, Coins, SignOut, Key, Files,
} from "@phosphor-icons/react";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { clearRepSession, getRepSession, repApi } from "@/lib/repApi";
import { MobileTopBar, MobileDrawer } from "@/components/MobileShell";

const NAV = [
  { to: "/representante",             end: true,  label: "Dashboard",  icon: ChartLineUp,  testid: "rep-nav-dashboard" },
  { to: "/representante/clientes",    end: false, label: "Clientes",   icon: Buildings,    testid: "rep-nav-clients" },
  { to: "/representante/comissoes",   end: false, label: "Comissões",  icon: Coins,        testid: "rep-nav-commissions" },
  { to: "/representante/materiais",   end: false, label: "Materiais",  icon: Files,        testid: "rep-nav-materials" },
  { to: "/representante/conta",       end: false, label: "Conta",      icon: Key,          testid: "rep-nav-account" },
];

// v0.96.19 — extraído pra reuso entre sidebar desktop e drawer mobile.
function RepNavContent({ repName, locationLabel, onLogout, onNavigate }) {
  return (
    <div className="flex-1 flex flex-col py-6 px-4 h-full">
      <div className="flex items-center gap-2.5 mb-8 px-2">
        <div className="w-8 h-8 rounded-md bg-white/10 flex items-center justify-center">
          <ShieldCheck size={16} weight="regular" />
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-[0.22em] text-slate-500 font-medium leading-none">
            AgentUS
          </div>
          <div className="font-heading text-[13px] font-semibold tracking-tight leading-tight mt-1">
            Representante
          </div>
        </div>
      </div>

      <nav className="flex-1 space-y-0.5">
        {NAV.map(({ to, end, label, icon: Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={onNavigate}
            data-testid={testid}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2 rounded-md text-[13px] transition-colors ${
                isActive
                  ? "bg-white/10 text-white"
                  : "text-slate-400 hover:text-white hover:bg-white/5"
              }`
            }
          >
            <Icon size={14} weight="regular" /> {label}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-white/5 pt-4 mt-4">
        <div className="px-2 mb-3">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-medium">
            Logado como
          </div>
          <div
            className="text-[13px] text-white truncate mt-1"
            data-testid="rep-current-name"
          >
            {repName}
          </div>
          {locationLabel && (
            <div className="text-[11px] text-slate-500 truncate">{locationLabel}</div>
          )}
        </div>
        <button
          onClick={onLogout}
          data-testid="rep-logout"
          className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-[13px] text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
        >
          <SignOut size={14} weight="regular" /> Sair
        </button>
      </div>
    </div>
  );
}

export default function RepShell() {
  const navigate = useNavigate();
  const session = getRepSession();
  const [valid, setValid] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (!session) {
      navigate("/representante/login", { replace: true });
      return;
    }
    Promise.all([
      repApi.get("/rep/me"),
      repApi.get("/rep/legal/status").catch(() => ({ data: { accepted: true } })),
    ]).then(
      ([_me, statusRes]) => {
        if (!statusRes.data.accepted) {
          navigate("/representante/aceitar-termos", { replace: true });
        } else {
          setValid(true);
        }
      },
      () => {
        clearRepSession();
        navigate("/representante/login", { replace: true });
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    clearRepSession();
    toast.success("Sessão encerrada.");
    navigate("/representante/login", { replace: true });
  };

  if (!session || valid === null) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center text-slate-500 text-sm">
        Carregando…
      </div>
    );
  }

  const rep = session.rep || {};
  const repName = rep.representative?.name || rep.name || "Representante";
  const repCity = rep.representative?.city;
  const repState = rep.representative?.state;
  const locationLabel = [repCity, repState].filter(Boolean).join(" · ");

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-slate-100 font-body md:flex" data-testid="rep-shell">
      <MobileTopBar
        title="Representante"
        onMenu={() => setDrawerOpen(true)}
      />

      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <RepNavContent
          repName={repName}
          locationLabel={locationLabel}
          onLogout={logout}
          onNavigate={() => setDrawerOpen(false)}
        />
      </MobileDrawer>

      <aside className="hidden md:flex w-60 shrink-0 border-r border-white/5 flex-col sticky top-0 h-screen">
        <RepNavContent
          repName={repName}
          locationLabel={locationLabel}
          onLogout={logout}
        />
      </aside>

      <main className="flex-1 min-w-0 overflow-x-hidden">
        <Outlet />
      </main>
      <Toaster theme="dark" position="top-right" richColors />
    </div>
  );
}
